import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { frontendOptions, gdqStatusOptions, workQueueRefreshApiTimeout } from "../../../lib/systemData";
import WorkQDeferredCustomSelect from "./WorkQDeferredCustomSelect";
import { useDispatch, useSelector } from "react-redux";
import { caseQuickUpdate } from "../../../lib/fetchApi";
import { notificationActions } from "../../../store/notifications";
import { workqueueActions } from "../../../store/workqueue";
import { filterActions } from "../../../store/filter";
import { workQFunctions } from "../../../lib/workQ";
import { aicaDataOptions } from "../../../assets/data";
import { aicaDepartmentFunctions } from "../../../lib/aicaDepartment";
import { diffCalendarDays, parseApiDateOrDateTime } from "../../../lib/dateParsing";
import classes from './GDQStatusCell.module.css';
import { useWorkQShell } from "../context/workQShellContext";

function isEmptyGdqStatus(value) {
    return value == null || value === ""
}

function caseStatusUpdate(caseId, field, value) {
    return {
        case_id: caseId,
        folder: "case",
        item: {
            fields: [{ field, value }],
        },
    }
}

function GDQStatusCell({data, item, caseData}) {

    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const selectedWorkQueue = useSelector(state => state.workqueue.selectedWorkQueue)
    const workqSlug = selectedWorkQueue?.slug || selectedWorkQueue
    const shell = useWorkQShell()
    const [localValue, setLocalValue] = useState(data)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (isSaving) return
        if (isEmptyGdqStatus(data) && !isEmptyGdqStatus(localValue)) return
        setLocalValue(data)
    }, [data, isSaving, localValue])

    const updateHandler = useCallback(async (payload, previousValue, nextValue) => {
        setIsSaving(true)

        const reqBody = {
            type: payload.identifier.type,
            type_id: payload.identifier.type_id,
            workq: workqSlug,
            fields: {
                [payload.identifier.field]: nextValue,
            },
        }

        const update = await caseQuickUpdate(userToken, payload.identifier.case_id, reqBody, null, true)

        if (update && update.errorMessage) {
            setLocalValue(previousValue)
            dispatch(filterActions.updateItemInWorkQRow(caseStatusUpdate(payload.identifier.case_id, payload.identifier.field, previousValue)))
            dispatch(notificationActions.setError(update))
        } else {
            dispatch(notificationActions.setError({errorMessage: `Your changes have been successfully saved to the case.`, type: 'success'}))
            dispatch(filterActions.updateItemInWorkQRow(caseStatusUpdate(payload.identifier.case_id, payload.identifier.field, nextValue)))
            setTimeout(() => {
                dispatch(workqueueActions.setRefreshApi())
            }, workQueueRefreshApiTimeout());
        }

        setIsSaving(false)
    }, [workqSlug, userToken, dispatch])

    const onChange = useCallback((payload) => {
        const nextValue = payload?.data?.value ?? null
        if (nextValue == 9) { // 9 is the value for "Other"
            shell?.openModal({ kind: 'gdq_other', caseData })
            return
        }

        const previousValue = localValue
        setLocalValue(nextValue)
        dispatch(filterActions.updateItemInWorkQRow(caseStatusUpdate(payload.identifier.case_id, payload.identifier.field, nextValue)))
        updateHandler(payload, previousValue, nextValue)
    }, [caseData, dispatch, localValue, shell, updateHandler])

    function getDaysSinceStatusDate(statusDate) {
        if (!statusDate) {
            return null
        }

        // Support both API-style (`YYYY-MM-DD`, `YYYY-MM-DD HH:mm:ss`) and the
        // legacy "MM/DD/YYYY" the cell occasionally receives.
        let compareDate = parseApiDateOrDateTime(statusDate)
        if (!compareDate && typeof statusDate === 'string' && statusDate.includes('/')) {
            const [mm, dd, yyyy] = statusDate.split('/').map(part => Number(part))
            if (!Number.isNaN(mm) && !Number.isNaN(dd) && !Number.isNaN(yyyy)) {
                // Keep as date-only string so diffCalendarDays applies app timezone consistently.
                compareDate = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
            }
        }
        if (!compareDate || Number.isNaN(compareDate.getTime())) {
            return null
        }

        const diff = diffCalendarDays(compareDate, new Date())
        if (diff == null || diff < 0) return 0
        return diff
    }

    function getColorKeyFromRangeConfig(days, colorConfig) {
        if (days == null || !colorConfig) {
            return null
        }

        const normalizeBound = (value) => {
            if (value == null) return null
            if (typeof value === 'string') {
                const trimmed = value.trim().toLowerCase()
                if (trimmed === '' || trimmed === 'null' || trimmed === 'none' || trimmed === 'undefined') {
                    return null
                }
            }
            const n = Number(value)
            return Number.isFinite(n) ? n : null
        }

        const normalizedDays = Number(days)
        if (!Number.isFinite(normalizedDays)) {
            return null
        }

        // Support both shapes:
        // - Object: { yellow: {min,max}, orange: {min,max} }
        // - Array:  [ { colorKey: 'orange', min, max }, ... ] (or similar)
        const entries = Array.isArray(colorConfig)
            ? colorConfig.map((item, idx) => [item?.colorKey ?? item?.key ?? item?.color ?? String(idx), item])
            : Object.entries(colorConfig)

        for (const [colorKeyRaw, rangeRaw] of entries) {
            const colorKey =
                typeof colorKeyRaw === 'string' ? colorKeyRaw.trim().toLowerCase() : String(colorKeyRaw)

            // allow nesting like { range: { min, max } }
            const range = rangeRaw?.range ?? rangeRaw

            const min = normalizeBound(range?.min)
            const max = normalizeBound(range?.max)

            // If max is null, treat it as open-ended (+∞). If min is null, treat it as (-∞).
            const lower = min == null ? -Infinity : min
            const upper = max == null ? Infinity : max

            // Invalid/meaningless range
            if (lower === -Infinity && upper === Infinity) {
                continue
            }

            if (normalizedDays >= lower && normalizedDays <= upper) {
                return colorKey
            }
        }

        return null
    }

    const configOptions = useMemo(() => frontendOptions(), []);
    const getGdqColorClass = useCallback((caseItem) => {
        
        if (!caseItem) {
            return ''
        }

        const days = getDaysSinceStatusDate(caseItem?.work_queue_gdq_mrq?.gdq_entry_date)
       
        let colorKey = null
        if (caseItem?.case?.gdq_status_id == 1) { // Pending Discharge
            colorKey = getColorKeyFromRangeConfig(days, configOptions?.PENDING_DISCHARGE_COLOR)
        } else if (caseItem?.case?.gdq_status_id == 2) { // Review Pending
            colorKey = getColorKeyFromRangeConfig(days, configOptions?.REVIEW_PENDING_COLOR)
        }
        

        if (colorKey == 'yellow') {
            return 'table_row_yellow_background'
        }
        if (colorKey == 'orange') {
            return 'table_row_orange_background'
        }

        return ''
    }, [configOptions])


    const optionalClasses = useMemo(() => {
        let nextClass = ''
        if (workQFunctions.numberOfDaysSinceLastVisitHasPassed(caseData?.case)) {
            nextClass = 'table_row_yellow_background';
        }
        const hasAicaHoldOverdue = aicaDataOptions.some((item) => {
            return aicaDepartmentFunctions.isStatusHoldAndDaysHavePassed(
                caseData?.aica_department_status,
                item.prefix,
            );
        });
        if (hasAicaHoldOverdue) {
            nextClass = 'table_row_orange_background';
        }
        const gdqColorClass = getGdqColorClass(caseData)
        if (gdqColorClass) {
            nextClass = gdqColorClass
        }
        return nextClass;
    }, [caseData, getGdqColorClass])


    return <>
        <div style={{position: 'relative', backgroundColor: optionalClasses}} className={optionalClasses ? optionalClasses : ''}>
            <WorkQDeferredCustomSelect
                fixedDrawer={true}
                getOptionsData={gdqStatusOptions}
                preSelectedValue={localValue}
                action={onChange}
                name={'gdq_status_id'}
                identifier={{type: 'case', field: 'gdq_status_id', type_id: null, folder: null, case_id: item.case?.id}}
                sameSizeAsContainer={true}
                minWidth={150}
                isDisabled={localValue == 2}
                isLoading={isSaving}
                ignoreDeactivatedOptions={true}
                hideOption={'is_other_status'}
                hoverMessage={caseData?.case?.other_gdq_status ? String(caseData.case.other_gdq_status) : ""}
                hoverDisabled={!caseData?.case?.other_gdq_status || caseData?.case?.gdq_status_id != 13}
                shouldCollapseAfterAction={(p) => p?.data?.value != 9}
            />
        </div>
    </>
}

export default memo(GDQStatusCell);